<template>
    <div class="q-pa-md">
        <div class="filter-wrap row q-col-gutter-sm items-center">
            <div class="col-shrink">
                <q-select
                    v-model="filter.activeState"
                    :options="activeStates"
                    option-value="value"
                    option-label="label"
                    bg-color="white"
                    outlined
                    dense
                    emit-value
                    map-options
                    @input="resetPagination"
                />
            </div>
            <div class="col">
                <q-input
                    v-model="filter.search"
                    :label="$t('general.search')"
                    bg-color="white"
                    debounce="300"
                    outlined
                    dense
                    clearable
                    @input="resetPagination"
                >
                    <template v-slot:append>
                        <q-icon name="search" />
                    </template>
                </q-input>
            </div>
            <div class="col-auto">
                <q-btn
                    color="secondary"
                    :label="$t('profile.add_employee')"
                    @click="showDialog"
                />
            </div>
        </div>
        <div class="q-gutter-md">
            <q-table
                :data="employees"
                :columns="columns"
                :pagination.sync="pagination"
                :rows-per-page-options="rowsPerPageOptions"
                :loading="isFetchingAffiliates"
                binary-state-sort
                row-key="id"
                flat
                @request="request"
            >
                <template v-slot:body-cell-start_date="props">
                    <q-td key="start_date" :props="props">
                        {{ formatDate(props.row.start_date) }}
                    </q-td>
                </template>
                <template v-slot:body-cell-end_date="props">
                    <q-td key="end_date" :props="props">
                        {{ formatDate(props.row.end_date) }}
                    </q-td>
                </template>
            </q-table>
        </div>
        <AddEmployee
            :show="dialog.show"
            @toggle-dialog="toggleDialog"
            @refresh="resetPagination"
        />
    </div>
</template>

<script>
import notify from '@/local-shared/mixins/notify'
import AddEmployee from '@/components/AddEmployee'
import affiliationsService from '@/services/modules/profile/affiliations'
import formatDate from '@/helpers/date-time'
import store from '@/store'

const loadingKeys = {
    fetchingAffiliates: 'fetching-affiliates',
}

export default {
    name: 'Employees',

    components: {
        AddEmployee,
    },

    mixins: [notify],

    data() {
        return {
            employees: [],

            pagination: {
                sortBy: 'first_name',
                descending: true,
                page: 1,
                rowsPerPage: 10,
                rowsNumber: 0,
            },

            rowsPerPageOptions: [5, 10, 25, 50, 100],

            filter: {
                search: '',
                activeState: true,
            },

            dialog: {
                show: false,
            },
        }
    },

    computed: {
        locale() {
            return store.getters['locale/currentLocale']
        },

        columns() {
            return [
                {
                    name: 'first_name',
                    align: 'left',
                    label: this.$t('general.name'),
                    field: (row) => row.users.first_name,
                    sortable: true,
                },
                // {
                //     name: 'last_name',
                //     align: 'left',
                //     label: this.$t('general.last_name'),
                //     field: (row) => row.users.last_name,
                //     sortable: true,
                // },
                {
                    name: 'email',
                    align: 'left',
                    label: this.$t('general.email'),
                    field: (row) => row.users.email,
                    sortable: true,
                },
                {
                    name: 'start_date',
                    align: 'left',
                    label: this.$t('general.affiliation_start_date'),
                    field: 'start_date',
                    sortable: true,
                },
                {
                    name: 'end_date',
                    align: 'left',
                    label: this.$t('general.affiliation_end_date'),
                    field: 'end_date',
                    sortable: true,
                },
            ]
        },

        activeStates() {
            return [
                {
                    value: true,
                    label: this.$t('general.is_active'),
                },
                {
                    value: false,
                    label: this.$t('general.not_active'),
                },
            ]
        },

        isLoading() {
            return this.isFetchingAffiliates
        },

        isFetchingAffiliates() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingAffiliates
            ).length
        },

        user() {
            return store.getters['session/user']
        },
    },

    async created() {
        this.tab = 'active'
        await this.fetchAffiliates()
    },

    methods: {
        resetPagination() {
            this.pagination.page = 1
            this.fetchAffiliates()
        },

        request(props) {
            const { page, rowsPerPage, sortBy, descending } = props.pagination
            // const filter = props.filter

            this.pagination.page = page
            this.pagination.rowsPerPage = rowsPerPage
            this.pagination.sortBy = sortBy
            this.pagination.descending = descending

            // this.filter = filter

            // this.$emit('request', this.paginationAux, this.filterAux)
            this.fetchAffiliates()
        },

        mapColumn(value) {
            return value == null ? this.pagination.sortBy : value
        },

        mapOrder(value) {
            return value == true ? 'desc' : 'asc'
        },

        async fetchAffiliates() {
            this.loadingAdd(loadingKeys.fetchingAffiliates)

            let parameters = {
                filter: {
                    active: this.filter.activeState,
                    search: this.filter.search,
                    activeUsers: true,
                    clientId: this.user.affiliations[0].client_id,
                    userRoleId: 3, //TODO: change this to be dinamyc to fecth student role id
                },
                pagination: {
                    perPage: this.pagination.rowsPerPage,
                    currentPage: this.pagination.page,
                },
                orderBy: [
                    {
                        column: this.mapColumn(this.pagination.sortBy),
                        direction: this.mapOrder(this.pagination.descending),
                    },
                ],
            }
            let response = await affiliationsService.users.retrieve(parameters)

            if (response.status === 200) {
                this.employees = response.data.results
                this.pagination.rowsNumber = response.data.pagination.total
            } else {
                this.notifyError(response.data.message)
            }

            this.loadingRemove(loadingKeys.fetchingAffiliates)
        },

        dateMaskFormat() {
            return this.locale &&
                this.locale.definition &&
                this.locale.definition.dateFormat
                ? this.locale.definition.dateFormat
                : 'DD/MM/YYYY'
        },

        formatDate(value) {
            return value
                ? formatDate.fromServerToUxString(
                      value,
                      'date',
                      this.dateMaskFormat()
                  )
                : null
        },

        showDialog() {
            this.dialog.show = true
        },

        toggleDialog() {
            this.dialog.show = !this.dialog.show
        },
    },
}
</script>
